import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  CompanyResponse,
  UserEntityRelationWithoutUserDataResponse,
  UserResponse
} from '../../../../@shared/models/response.module';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {UserService} from '../../../../@core/services/user.service';
import {CompanyService} from '../../../../@shared/services/company.service';
import {forkJoin, of, Subscription} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Router} from '@angular/router';
import {CreateOrganisation} from './components/create-organisation/create-organisation';
import {StorageService} from '../../../../@core/services/storage.service';
import {TranslateService} from '@ngx-translate/core';

interface UserCompany {
  company: CompanyResponse;
  role_id: number;
  role_name: string;
}

@Component({
  selector: 'app-user-profile-organisations',
  standalone: false,
  templateUrl: './user-profile-organisations.html',
  styleUrl: './user-profile-organisations.css'
})
export class UserProfileOrganisations implements OnInit, OnDestroy {
  @Input() user: UserResponse
  userCompanies: UserCompany[] = []
  loading: boolean = false
  private companyListSubscription: Subscription | undefined;

  constructor(
    private modal: NgbModal,
    private userService: UserService,
    private companyService: CompanyService,
    private router: Router,
    private storageService: StorageService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.loadUserOrganisations()

    // Subscribe to company list changes (e.g., when a company is deleted)
    this.companyListSubscription = this.companyService.companyListChanged$.subscribe(() => {
      this.loadUserOrganisations();
    });
  }

  ngOnDestroy() {
    if (this.companyListSubscription) {
      this.companyListSubscription.unsubscribe();
    }
  }

  loadUserOrganisations() {
    this.loading = true

    // Get user's entity relations (companies)
    this.userService.getUserEntityRelation().subscribe({
      next: (entities) => {
        if (entities && entities.length > 0) {
          // Create an array of observables to fetch company details
          // Use public endpoint to avoid JWT token race conditions after creating a company
          const companyRequests = entities.map(entity =>
            this.companyService.getPublicCompanyById(entity.entity_id).pipe(
              map(company => ({
                company: company,
                role_id: entity.role_id,
                role_name: entity.role_name
              })),
              catchError(() => of(null))
            )
          )

          // Execute all requests in parallel
          forkJoin(companyRequests).subscribe({
            next: (companies) => {
              this.userCompanies = companies.filter(c => c !== null) as UserCompany[]
              this.loading = false
            },
            error: () => {
              this.loading = false
            }
          })
        } else {
          this.userCompanies = []
          this.loading = false
        }
      },
      error: () => {
        this.loading = false
      }
    })
  }

  createNewOrganisation() {
    const modalRef = this.modal.open(CreateOrganisation, {size: 'lg', centered: true})

    modalRef.result.then((result) => {
      if (result) {
        // Reload organisations after successful creation
        this.loadUserOrganisations()
      }
    }).catch(() => {
      // Modal dismissed
    })
  }

  editOrganisation(company: CompanyResponse) {
    // TODO: Open edit organisation modal or navigate to company profile
    console.log('Edit organisation:', company.company_id)
    // Navigate to company profile page
    this.router.navigate(['/company-profile'], {
      queryParams: { companyId: company.company_id }
    })
  }

  viewOrganisation(userCompany: UserCompany) {
    const payload: UserEntityRelationWithoutUserDataResponse = {
      entity_id: userCompany.company.company_id,
      entity_name: userCompany.company.company_name,
      role_id: userCompany.role_id,
      role_name: userCompany.role_name,
      created_at: userCompany.company.created_at,
      updated_at: userCompany.company.updated_at,
      deleted_at: userCompany.company.deleted_at,
    }
    this.storageService.saveSelectedCompany(payload);
    // Navigate to company profile page
    this.router.navigate(['/company-profile'], {
      queryParams: { companyId: userCompany.company.company_id }
    })
  }

  getRoleBadgeClass(roleId: number): string {
    // 0 = Owner, 1 = Admin, 2 = Member, etc.
    switch (roleId) {
      case 0:
        return 'badge-primary'
      case 1:
        return 'badge-success'
      default:
        return 'badge-secondary'
    }
  }

  getOrganisationTypeLabel(companyType: string): string {
    switch (companyType) {
      case 'stable':
        return this.translateService.instant('organisations.typeStable');
      case 'riding_club':
        return this.translateService.instant('organisations.typeRidingClub');
      case 'riding_school':
        return this.translateService.instant('organisations.typeRidingSchool');
      case 'stores_services':
        return this.translateService.instant('organisations.typeStoresServices');
      default:
        return companyType || this.translateService.instant('organisations.typeOther');
    }
  }

  getOrganisationTypeIcon(companyType: string): string {
    switch (companyType) {
      case 'stable':
        return 'fa-warehouse';
      case 'riding_club':
        return 'fa-users';
      case 'riding_school':
        return 'fa-graduation-cap';
      case 'stores_services':
        return 'fa-store';
      default:
        return 'fa-building';
    }
  }
}
